// Demo-only mockup of the "FTB Automation" scenario's 5-step pipeline — the
// ICE NOC site-outage FTB (flash traffic bulletin) notification process
// described in "FTB Automation Information.docx". Each `section` renders
// one of the docx's own parts: the NOC Teams-chat pull, the SNOW Location
// table lookup (site essentiality), the SharePoint "Processing Centers and
// Surge Sites" lookup (the catch-all rule), the FTB requirement they
// combine into (decideFtb() in lib/ftbDecision.ts), and the generated
// notice text (buildFtbNotice()). Fully local/decorative: no real SNOW,
// SharePoint, or Teams call, mirroring how the other fan-out mockups
// (AIReportMockup, PagerDutyAlertMockup, etc.) preview a channel without a
// real integration behind it.
import {
  buildFtbNotice,
  decideFtb,
  lookupSite,
  FTB_DEMO_NOTIFICATION,
} from '../lib/ftbDecision';
import '../styles/ftb-notice-mockup.css';

export type FtbSection = 'notification' | 'snow-lookup' | 'sharepoint-lookup' | 'requirement' | 'notice';

const SECTION_TITLES: Record<FtbSection, string> = {
  notification: 'IC/NOC Group Chat',
  'snow-lookup': 'SNOW Location Table',
  'sharepoint-lookup': 'SharePoint Site List',
  requirement: 'FTB Requirement',
  notice: 'Generated FTB Notice',
};

export default function FtbAutomationMockup({
  section,
  onClose,
}: {
  section: FtbSection;
  onClose: () => void;
}) {
  const noc = FTB_DEMO_NOTIFICATION;
  const site = lookupSite(noc.siteCode);
  const siteName = site?.name || `Site ${noc.siteCode}`;
  const decision = decideFtb([noc.siteCode]);
  const notice = decision.required ? buildFtbNotice({ noc, siteName }) : null;

  return (
    <div className="ftb-mock-overlay" onClick={onClose}>
      <div className="ftb-mock-shell" onClick={(e) => e.stopPropagation()}>
        <div className="ftb-mock-toolbar">
          <span className="ftb-mock-toolbar-label">FTB Automation — {SECTION_TITLES[section]}</span>
          <button type="button" className="ftb-mock-close" onClick={onClose} aria-label="Close preview">
            ✕
          </button>
        </div>

        <div className="ftb-mock-page">
          {section === 'notification' && (
            <div className="ftb-mock-section">
              <div className="ftb-mock-caption">IC/NOC Group Chat — pull from notification</div>
              <dl className="ftb-mock-facts">
                <div><dt>Ticket</dt><dd>{noc.ticket}</dd></div>
                <div><dt>Site</dt><dd>{noc.siteCode} — {siteName}</dd></div>
                <div><dt>Type</dt><dd>{noc.type}</dd></div>
                <div><dt>Device</dt><dd>{noc.device}</dd></div>
                <div><dt>Initial</dt><dd>{noc.initial}</dd></div>
                <div><dt>Final</dt><dd>{noc.final || 'TBD'}</dd></div>
                <div><dt>POC</dt><dd>{noc.poc}</dd></div>
                <div><dt>Status</dt><dd>{noc.status}</dd></div>
              </dl>
            </div>
          )}

          {section === 'snow-lookup' && (
            <div className="ftb-mock-section">
              <div className="ftb-mock-caption">
                SNOW Location table — determine if the site is essential
              </div>
              <dl className="ftb-mock-facts">
                <div><dt>Site</dt><dd>{noc.siteCode}</dd></div>
                <div><dt>Location name</dt><dd>{siteName}</dd></div>
                <div><dt>City/State</dt><dd>{site?.cityState || '—'}</dd></div>
                <div><dt>Essential</dt><dd>{site?.essential ? 'Yes' : 'No'}</dd></div>
              </dl>
            </div>
          )}

          {section === 'sharepoint-lookup' && (
            <div className="ftb-mock-section">
              <div className="ftb-mock-caption">
                Processing Centers and Surge Sites — IC SharePoint workbook
              </div>
              <dl className="ftb-mock-facts">
                <div><dt>Site</dt><dd>{noc.siteCode}</dd></div>
                <div><dt>On site list tab</dt><dd>{site?.onSurgeList ? 'Yes' : 'No'}</dd></div>
              </dl>
              {site?.onSurgeList && (
                <p className="ftb-mock-reason">Any number of sites — site code on this list means an FTB is needed.</p>
              )}
            </div>
          )}

          {section === 'requirement' && (
            <div className="ftb-mock-section">
              <div className="ftb-mock-caption">FTB requirement</div>
              <div className={`ftb-mock-badge ${decision.required ? 'ftb-mock-badge--yes' : 'ftb-mock-badge--no'}`}>
                FTB {decision.required ? 'required' : 'not required'}
              </div>
              <p className="ftb-mock-reason">{decision.reason}</p>
              <ul className="ftb-mock-trail">
                {decision.trail.map((check) => (
                  <li key={check.rule} className={check.matched ? 'matched' : undefined}>
                    <span className="ftb-mock-trail-rule">{check.rule}</span>
                    <span className="ftb-mock-trail-detail">{check.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {section === 'notice' && (
            <div className="ftb-mock-section">
              <div className="ftb-mock-caption">Generated FTB notice</div>
              {notice ? (
                <dl className="ftb-mock-notice">
                  <div><dt>What is Broken</dt><dd>{notice.whatIsBroken}</dd></div>
                  <div><dt>Timeframe</dt><dd>{notice.timeframe}</dd></div>
                  <div><dt>Qty Service Desk Tickets</dt><dd>{notice.qtyServiceDeskTickets}</dd></div>
                  <div><dt>Operational Impact</dt><dd>{notice.operationalImpact}</dd></div>
                  <div><dt>Mitigations</dt><dd>{notice.mitigations}</dd></div>
                </dl>
              ) : (
                <p className="ftb-mock-reason">FTB not required for this notification — no notice generated.</p>
              )}
            </div>
          )}
        </div>

        <div className="ftb-mock-actionbar">
          <span className="ftb-mock-actionbar-note">
            Preview only — this demo does not submit a real FTB.
          </span>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
